import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Building2, MapPin, Send, Sparkles, UserRound } from "lucide-react";
import { trackFeatured, featuredHref } from "@/lib/featuredPro";

const initials = (n) => (n || "?").split(/\s+/).filter((w) => /^[A-Za-z]/.test(w)).map((x) => x[0]).slice(0, 2).join("").toUpperCase() || "?";
export const DEFAULT_FOCAL = { x: 0.5, y: 0.3 };
export const focalPosition = (f) => `${(f?.x ?? DEFAULT_FOCAL.x) * 100}% ${(f?.y ?? DEFAULT_FOCAL.y) * 100}%`;
const BTN = "inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl px-3 text-[13px] font-semibold transition-colors";

function Portrait({ e }) {
  return (
    <div className="relative aspect-[3/2] w-full overflow-hidden rounded-t-2xl bg-[#0F2C59]">
      {e.photo ? (
        <img src={e.photo} alt={e.name} loading="lazy" decoding="async" className="h-full w-full object-cover" style={{ objectPosition: focalPosition(e.photo_focal) }} />
      ) : (
        <div data-testid="featured-portrait-fallback" className="flex h-full w-full items-center justify-center font-display text-4xl font-bold text-[#C79A3B]">{initials(e.name)}</div>
      )}
    </div>
  );
}

function NameLine({ c, placement }) {
  const e = c.entity || {};
  const href = featuredHref(c);
  return (
    <h3 className="flex flex-wrap items-center gap-x-2 gap-y-1 font-display text-lg font-bold leading-tight text-[#0F2C59]">
      {href ? (
        <Link to={href} onClick={() => trackFeatured("click", c.id, placement)} data-testid="featured-name"
          className="min-w-0 break-words hover:text-[#A9802B] hover:underline underline-offset-2">{e.name}</Link>
      ) : <span className="min-w-0 break-words" data-testid="featured-name">{e.name}</span>}
      {e.is_verified && (
        <span data-testid="verified-badge" className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#1D63D8] px-2 py-0.5 font-sans text-[10px] font-semibold text-white">
          <BadgeCheck className="h-3.5 w-3.5" /> Verified
        </span>
      )}
    </h3>
  );
}

// Context count line ("6 active listings for sale") only when it differs from the total public count.
const isCountReason = (r) => /^\d/.test(r);
const showsContextCount = (e) => (e.relevance_reasons || []).some(isCountReason) && (e.relevant_listing_count || 0) !== (e.active_listing_count || 0);
const visibleReasons = (e) => (e.relevance_reasons || []).filter((r) => !isCountReason(r) || showsContextCount(e));

function ExploreLink({ c, placement }) {
  const e = c.entity || {};
  const n = e.active_listing_count || 0;
  const href = featuredHref(c);
  if (!n || !href) return null;
  const all = showsContextCount(e) ? "all " : "";
  return (
    <Link to={`${href}#properties`} onClick={() => trackFeatured("click", c.id, placement)} data-testid={`featured-explore-properties-${e.entity_id}`}
      className="group inline-flex items-center gap-1 text-[13px] font-semibold text-[#0F2C59] hover:text-[#A9802B]">
      Explore {all}{n} Propert{n === 1 ? "y" : "ies"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function Details({ c, placement }) {
  const e = c.entity || {};
  const loc = [e.city, e.province].filter(Boolean).join(", ");
  return (
    <div className="flex-1 px-4 pt-3">
      <NameLine c={c} placement={placement} />
      {e.role_label && <p className="mt-0.5 text-[13px] font-medium text-[#A9802B] line-clamp-1">{e.role_label}</p>}
      <div className="mt-2 space-y-1 text-[13px] text-slate-600">
        {e.organization && <p className="flex items-center gap-1.5 min-w-0"><Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="truncate">{e.organization}</span></p>}
        {loc && <p className="flex items-center gap-1.5 min-w-0"><MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" /><span className="truncate">{loc}</span></p>}
        {visibleReasons(e).map((r) => (
          <p key={r} className="flex items-center gap-1.5 font-medium text-[#7A5C1E]" data-testid="featured-relevance"><Sparkles className="h-3.5 w-3.5 shrink-0 text-[#C79A3B]" /><span className="truncate">{r}</span></p>
        ))}
      </div>
      <div className="mt-1.5"><ExploreLink c={c} placement={placement} /></div>
    </div>
  );
}

function Actions({ c, placement, extraAction }) {
  const e = c.entity || {};
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-2 px-4 pb-3">
      <Link to={featuredHref(c, true)} onClick={() => trackFeatured("inquiry_click", c.id, placement)} data-testid={`featured-send-inquiry-${e.entity_id}`}
        className={`${BTN} basis-full bg-[#C79A3B] text-[#0F2C59] shadow-sm hover:bg-[#D9AE4F]`}>
        <Send className="h-4 w-4" /> Send Inquiry
      </Link>
      <Link to={featuredHref(c)} onClick={() => trackFeatured("click", c.id, placement)} data-testid={`featured-view-profile-${e.entity_id}`}
        className={`${BTN} min-w-[7.5rem] flex-1 border border-[#0F2C59]/30 text-[#0F2C59] hover:bg-[#0F2C59]/5`}>
        <UserRound className="h-4 w-4" /> View Profile
      </Link>
      {extraAction && <div className="shrink-0">{extraAction}</div>}
    </div>
  );
}

// Premium Featured Professional card shared by every featured placement (one format everywhere).
export function FeaturedProCard({ c, placement, extraAction }) {
  const e = c.entity || {};
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { trackFeatured("impression", c.id, placement); return undefined; }
    const io = new IntersectionObserver(([en]) => {
      if (en.isIntersecting) { trackFeatured("impression", c.id, placement); io.disconnect(); }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [c.id, placement]);

  return (
    <article ref={ref} data-testid={`featured-pro-card-${e.entity_id}`}
      className="flex h-full flex-col rounded-2xl bg-white shadow-[0_14px_40px_-18px_rgba(10,30,60,0.55)] ring-1 ring-[#C79A3B]/30 transition-transform duration-300 hover:-translate-y-1">
      <Link to={featuredHref(c)} onClick={() => trackFeatured("click", c.id, placement)} aria-label={`View ${e.name}'s profile`} data-testid={`featured-pro-portrait-${e.entity_id}`}>
        <Portrait e={e} />
      </Link>
      <div className="flex min-w-0 flex-1 flex-col">
        <Details c={c} placement={placement} />
        <Actions c={c} placement={placement} extraAction={extraAction} />
      </div>
    </article>
  );
}

export default FeaturedProCard;
